var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useRef, useEffect, forwardRef, useImperativeHandle, createElement } from '@max/max';
import View from '@hfe/max-view';
import Image from '@hfe/max-image';
import { Animated } from '@max/mbc-animated';
import AnimatedView from '@max/mbc-animate-view';
import { rpx2px } from '@hfe/max-universal-unit-tool';
import './index.scss';
import { isInHarmony } from '@/utils';
// 常量定义
const PRIZE_HEIGHT = 200;
const PRIZE_OFFSET = 12;
const REEL_WIDTH = 120;
const REPEAT_COUNT = 8; // 重复渲染次数，确保无限滚动
const SPIN_TIME = 2200; // 抽奖滚动时间2秒
const BOUNCE_TIME = 160; // 回弹时间 160ms
const BOUNCE_OFFSET = 6; // 回弹偏移量 6px
// const CENTER_OFFSET = rpx2px(PRIZE_HEIGHT) * 0.5;
const CENTER_OFFSET = 0;
const INIT_POSITION = rpx2px(-848);
const SlotMachine = forwardRef((props, ref) => {
    const { reelImage = [], prizeCount = 9, winningIndexes = [], onStart, onEnd, onReset, slotMachineSpinController, style } = props;
    // 动画值
    const animatedPosY1 = useRef(new Animated.Value(INIT_POSITION)).current;
    const animatedPosY2 = useRef(new Animated.Value(INIT_POSITION)).current;
    const animatedPosY3 = useRef(new Animated.Value(INIT_POSITION)).current;
    const animatedPosY4 = useRef(new Animated.Value(INIT_POSITION)).current;
    const animatedPosY5 = useRef(new Animated.Value(INIT_POSITION)).current;
    // 暴露 resetSpin 方法给父组件
    useImperativeHandle(ref, () => ({
        resetSpin: () => {
            onReset && onReset();
            animatedPosY1.setValue(INIT_POSITION);
            animatedPosY2.setValue(INIT_POSITION);
            animatedPosY3.setValue(INIT_POSITION);
            animatedPosY4.setValue(INIT_POSITION);
            animatedPosY5.setValue(INIT_POSITION);
        }
    }), [onReset]);
    // 监听抽奖控制器
    useEffect(() => {
        if (slotMachineSpinController > 0) {
            startSpin();
        }
    }, [slotMachineSpinController]);
    // 计算终点位置，确保中奖奖品显示在中心
    const calculateEndPosition = (reelIndex) => {
        const cycles = 3;
        const prizeHeight = rpx2px(PRIZE_HEIGHT);
        const prizeOffset = rpx2px(PRIZE_OFFSET);
        const winningIndex = winningIndexes[reelIndex];
        // 单个长图的实际高度
        // 长图结构：9个奖品，前8个间距有12rpx，最后一个没有间距
        // 实际高度 = 奖品总高度 + 间距总高度 = 200*9 + 12*8 = 1896rpx
        const singleReelHeight = prizeHeight * prizeCount + prizeOffset * (prizeCount - 1);
        if (winningIndex === undefined || !winningIndexes.length || winningIndex < 0) {
            // 默认停在第一个奖品位置
            return singleReelHeight * cycles + CENTER_OFFSET;
        }
        // 滚动 cycles 圈 + 到达目标奖品的位置
        // 目标奖品位置 = winningIndex * (prizeHeight + prizeOffset)
        const targetPrizePosition = winningIndex * (prizeHeight + prizeOffset);
        const endPosition = singleReelHeight * cycles + targetPrizePosition + CENTER_OFFSET;
        return endPosition;
    };
    // 创建速率曲线
    const createCustomSpeedCurve = () => {
        return (t) => {
            if (t <= 0.098) {
                const progress = t / 0.098;
                return progress * 0.15;
            }
            else if (t <= 0.49) {
                const progress = (t - 0.098) / 0.392;
                return 0.15 + 0.35 * Math.pow(progress, 0.6);
            }
            else if (t <= 0.83) {
                const progress = (t - 0.49) / 0.34;
                return 0.5 + 0.25 * Math.pow(progress, 1.2);
            }
            else if (t <= 0.98) {
                const progress = (t - 0.83) / 0.15;
                return 0.75 + 0.15 * Math.pow(progress, 1.8);
            }
            else {
                const progress = (t - 0.98) / 0.02;
                return 0.9 + 0.1 * progress;
            }
        };
    };
    // 快速滚动
    const startSpin = () => __awaiter(void 0, void 0, void 0, function* () {
        onStart && onStart();
        // 验证终点位置计算
        // winningIndexes.forEach((index, reelIndex) => {
        //   const endPos = calculateEndPosition(reelIndex);
        //   // console.log(`轮子${reelIndex}: winningIndex=${index}, endPosition=${endPos}`);
        // });
        const animateReelWithCustomCurve = (animatedValue, reelIndex, delay) => {
            const endPosition = calculateEndPosition(reelIndex);
            const bouncePosition = endPosition + BOUNCE_OFFSET;
            setTimeout(() => {
                // 第一阶段：使用自定义曲线滚动到回弹位置
                Animated.timing(animatedValue, {
                    toValue: -bouncePosition,
                    duration: SPIN_TIME - BOUNCE_TIME,
                    easing: createCustomSpeedCurve(),
                    useNativeDriver: true
                }).start(() => {
                    // console.log(`快速滚动轮子${reelIndex}到达回弹位置: -${bouncePosition}`);
                    // 第二阶段：回弹到最终位置
                    Animated.timing(animatedValue, {
                        toValue: -endPosition,
                        duration: BOUNCE_TIME,
                        easing: Animated.Easing.out(Animated.Easing.quad),
                        useNativeDriver: true
                    }).start(() => {
                        // console.log(`轮子${reelIndex}最终停止位置: -${endPosition}`);
                    });
                });
            }, delay);
        };
        // 从个位数开始，间隔320ms依次启动动画
        animateReelWithCustomCurve(animatedPosY5, 4, 1280);
        animateReelWithCustomCurve(animatedPosY4, 3, 960);
        animateReelWithCustomCurve(animatedPosY3, 2, 640);
        animateReelWithCustomCurve(animatedPosY2, 1, 320);
        animateReelWithCustomCurve(animatedPosY1, 0, 0);
        const totalAnimationTime = 1280 + SPIN_TIME;
        setTimeout(() => {
            onEnd && onEnd();
        }, totalAnimationTime);
    });
    // 渲染单个轮子 - 增加足够的重复以支持长距离滚动
    const renderReel = (animatedValue, reelIndex) => {
        // 长图实际高度：9个奖品 + 8个间距（最后一个奖品下面没有间距）
        // 200*9 + 12*8 = 1896rpx
        const totalImageHeight = PRIZE_HEIGHT * prizeCount + PRIZE_OFFSET * (prizeCount - 1);
        return <View key={`reel-${reelIndex}`} className="slot-machine__reel">
        <AnimatedView style={{
            width: `${REEL_WIDTH}rpx`,
            height: `${totalImageHeight * REPEAT_COUNT}rpx`,
            transform: [{
                    translateY: animatedValue
                }],
            marginTop: isInHarmony ? '2rpx' : '0rpx'
        }}>
          
          {Array.from({
            length: REPEAT_COUNT
        }, (_, index) => <Image key={`image-${index}`} source={{
            uri: reelImage[reelIndex]
        }} style={{
            width: `${REEL_WIDTH}rpx`,
            height: `${totalImageHeight}rpx`
        }} resizeMode="cover"/>)}
        </AnimatedView>
      </View>;
    };
    return <View className="slot-machine__container" style={style}>
      <View className="slot-machine__reels-container">
        {renderReel(animatedPosY1, 0)}
        {renderReel(animatedPosY2, 1)}
        {renderReel(animatedPosY3, 2)}
        {renderReel(animatedPosY4, 3)}
        {renderReel(animatedPosY5, 4)}
      </View>
    </View>;
});
export default SlotMachine;
